import { env } from '../../config/env'
import { DataLabel } from '@120wateraudit/envirio-components/dist/components/DataLabel'
import { DataValue } from '@120wateraudit/envirio-components/dist/components/DataValue'
import { GoogleMap, Loader } from '@120wateraudit/envirio-components'
import styles from './FacilityDetailsCard.module.scss'
import { FacilityDetailsQuery } from '../../rpcs/FacilityDetailsQuery'
import { WaterTestingMapPinInfoQuery } from '../../rpcs/WaterTestingMapPinInfoQuery'
import { Responsive } from '../Responsive/Responsive'

interface FacilityDetailsProps {
  facilityId: number
}

interface Address {
  address: string | null
  city: string | null
  state: string | null
  zip: string | null
}

/**
 * SUP-247: `facilityDetails` returns the geocoded address columns
 * (Schools.GeocodedAddress/City/State/Zip), not the stored ones, so the detail
 * view disagreed with the search results and the map pin — sometimes by a whole
 * street. Geocoding is best-effort: it abbreviates ("Road" -> "Rd"), drops suite
 * numbers, occasionally rewrites the ZIP, and for a vague input can land on an
 * entirely different property.
 *
 * `waterTestingMapPinInfo` returns the same facility's stored address, and is
 * what the results grid and the marker popup already display. Read the address
 * from there so all three agree, and keep `facilityDetails` for latitude,
 * longitude and website.
 *
 * This is a client-side correction. The underlying problem is that the
 * `facilityDetails` resolver selects the wrong columns, and separately that
 * geocoding-api accepts poor matches (it validates only the state). Both belong
 * in their own tickets.
 */
export const FacilityDetailsCard = ({
  facilityId
}: FacilityDetailsProps): JSX.Element => (
  <FacilityDetailsQuery variables={{ facilityId }}>
    {({ data, loading, error }) => {
      if (loading) {
        return <Loader />
      }

      if (error || !data || !data.facilityDetails) {
        return null
      }

      const { latitude, longitude, website } = data.facilityDetails

      // Fall back to the geocoded values if the stored address cannot be
      // fetched — a slightly wrong address still beats an empty card.
      const geocodedFallback: Address = {
        address: data.facilityDetails.address,
        city: data.facilityDetails.city,
        state: data.facilityDetails.state,
        zip: data.facilityDetails.zip
      }

      return (
        <WaterTestingMapPinInfoQuery variables={{ facilityId }}>
          {({ data: pinData, loading: pinLoading, error: pinError }) => {
            if (pinLoading) {
              return <Loader />
            }

            const stored = pinError ? null : pinData && pinData.info
            const { address, city, state, zip } = stored
              ? {
                  address: stored.address,
                  city: stored.city,
                  state: stored.state,
                  zip: stored.zip
                }
              : geocodedFallback

            const show = getLocationDetailsVisibilityReport({
              latitude,
              longitude,
              website,
              address,
              city,
              state,
              zip
            })

            if (!show.card) {
              return null
            }

            return (
              <div className={styles.detailsCard}>
                <h3>Location Details</h3>
                <Responsive platform="desktop">
                  <div className={styles.mapContainer}>
                    {show.map && (
                      <GoogleMap
                        apiKey={env.googleMapsApiKey}
                        mapId={env.googleMapId}
                        defaultZoom={5}
                        center={{
                          lat: latitude || 40.7128,
                          lng: longitude || 74.006
                        }}
                        markers={
                          latitude && longitude
                            ? [
                                {
                                  position: { lat: latitude, lng: longitude },
                                  info: {}
                                }
                              ]
                            : []
                        }
                      />
                    )}
                  </div>
                </Responsive>
                {show.address && (
                  <div className={styles.addressSection}>
                    <DataLabel>Address</DataLabel>
                    <DataValue>
                      {address}
                      <br />
                      {city && city}
                      {city && state && ','}
                      {state && <>&nbsp; {state}</>}
                      {zip && <>&nbsp; {zip}</>}
                    </DataValue>
                  </div>
                )}
                {show.website && website && (
                  <div className={styles.websiteSection}>
                    <DataLabel>Website</DataLabel>
                    <a className={styles.link} target="_blank" href={website}>
                      {website || '--'}
                    </a>
                  </div>
                )}
              </div>
            )
          }}
        </WaterTestingMapPinInfoQuery>
      )
    }}
  </FacilityDetailsQuery>
)

interface LocationDetailsShowingReport {
  map: boolean
  address: boolean
  website: boolean
  card: boolean
}

interface VisibilityInput extends Address {
  latitude: number | null
  longitude: number | null
  website: string | null
}

/**
 * Takes the resolved values rather than the raw query response, so the address
 * section is decided by the address actually being rendered.
 */
const getLocationDetailsVisibilityReport = ({
  latitude,
  longitude,
  address,
  website,
  city,
  state,
  zip
}: VisibilityInput): LocationDetailsShowingReport => {
  const report: LocationDetailsShowingReport = {
    map: false,
    address: false,
    website: false,
    card: false
  }

  if (longitude && latitude) {
    report.map = true
  }
  if (address || (city && state) || zip) {
    report.address = true
  }
  if (website) {
    report.website = true
  }
  if (!report.address && !report.map && !report.website) {
    return report
  }
  report.card = true
  return report
}
